import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { TemplateParamCatalogOutput, VehicleDetail } from 'used-vehicle-contracts';
import { orpc } from '../../../apis/orpc-query';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { DefinitionList, StatCard } from '../../../components/ui/DefinitionList';
import { FormField } from '../../../components/ui/FormField';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Sheet } from '../../../components/ui/Sheet';
import { formatDay, formatMoney, formatPercent } from '../../../lib/format';
import { serverMessage, submitErrorKey } from '../../../lib/form-helpers';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { CostItemsCard } from '../../costs/CostItemsCard';
import { toOptionalNumber } from '../../estimates/estimates-display';
import { TradeInSheet } from './TradeInSheet';
import { TotalCostCard } from './TotalCostCard';

export interface VehicleCostsTabProps {
  vehicle: VehicleDetail;
  editable: boolean;
  onChanged: () => Promise<void>;
}

/** The overridable values the Costs tab lets a person move for this vehicle alone (plan §5.10). */
const OVERRIDE_KEYS = ['commissionFee', 'generalFee', 'registrationFee', 'fuelLiters', 'expectedHoldingDays', 'riskBuffer'] as const;

interface OfferForm {
  profitMode: string;
  targetProfit: string;
  targetMarginPercent: string;
  riskBuffer: string;
  askingPrice: string;
}

/**
 * Costs & offer target (plan §7, PP §7.2).
 *
 * Three groups of money, in the order they become true: what a person **decides** (the profit target, the
 * risk buffer, the asking price), what the workshop **books** (the work items), and what the equations
 * **work out** (the automatic components). Every one of them ends in the same required-retail figure, so
 * the tab shows the arithmetic rather than a conclusion.
 */
export const VehicleCostsTab = ({ vehicle, editable, onChanged }: VehicleCostsTabProps) => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();

  const [isOfferOpen, setOfferOpen] = useState(false);
  const [isTradeInOpen, setTradeInOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const computeQuery = useQuery(orpc.valuations.compute.queryOptions({ input: { owner: { type: 'VEHICLE', id: vehicle.id } } }));
  const catalogQuery = useQuery(orpc.templates.paramCatalog.queryOptions());
  const templateVersionQuery = useQuery(
    orpc.templates.getVersion.queryOptions({
      input: { versionId: vehicle.template?.versionId ?? '' },
      enabled: Boolean(vehicle.template?.versionId),
    }),
  );
  const update = useMutation(orpc.vehicles.update.mutationOptions());

  const [form, setForm] = useState<OfferForm>(() => offerFormFrom(vehicle));

  useEffect(() => setForm(offerFormFrom(vehicle)), [vehicle]);

  const outputs = computeQuery.data?.outputs ?? {};
  const inputs = computeQuery.data?.inputs ?? {};
  const provenance = computeQuery.data?.provenance ?? {};
  const templateParams = templateVersionQuery.data?.params ?? {};

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: orpc.valuations.compute.key() });
    await onChanged();
  };

  const saveOffer = async () => {
    setError(null);

    const marginPercent = toOptionalNumber(form.targetMarginPercent);

    try {
      await update.mutateAsync({
        vehicleId: vehicle.id,
        expectedRevision: vehicle.revision,
        profitMode: form.profitMode as VehicleDetail['profitMode'],
        askingPrice: toOptionalNumber(form.askingPrice),
        paramOverrides: {
          targetProfit: { value: toOptionalNumber(form.targetProfit) },
          targetMargin: { value: marginPercent === null ? null : Math.round(marginPercent * 100) / 10_000 },
          riskBuffer: { value: toOptionalNumber(form.riskBuffer) },
        },
      });

      await refresh();
      setOfferOpen(false);
    } catch (caught: unknown) {
      setError(serverMessage(caught) ?? t(submitErrorKey(caught)));
    }
  };

  const saveOverride = async (key: string, value: number | null) => {
    setError(null);

    try {
      await update.mutateAsync({ vehicleId: vehicle.id, expectedRevision: vehicle.revision, paramOverrides: { [key]: { value } } });
      await refresh();
    } catch (caught: unknown) {
      setError(serverMessage(caught) ?? t(submitErrorKey(caught)));
    }
  };

  /** The equations that carry a cost category, with what they worked out and what the items resolved to. */
  const automatic = (computeQuery.data?.trace ?? []).filter((entry) => entry.cost !== undefined);

  const sale = vehicle.sale;
  // What is still an estimate on a sold car: the rows the "unresolved costs" queue is counting.
  const unresolved = (computeQuery.data?.costResolution ?? [])
    .flatMap((resolution) => resolution.items)
    .filter((item) => item.provisional)
    .map((item) => item.name ?? item.category);

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
      <div className="space-y-4">
        {sale === null ? null : (
          <section className="space-y-3 rounded-lg border border-border bg-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-card-foreground">{t('vehicles.completedSaleTitle')}</h2>
              {vehicle.realizedContribution?.provisional ? <Badge tone="warning">{t('costs.provisional')}</Badge> : null}
            </div>
            <p className="text-sm text-muted-foreground">{t('vehicles.completedSaleBody')}</p>
            <DefinitionList
              items={[
                { label: t('vehicles.salePrice'), value: formatMoney(sale.salePrice, { exact: true }) },
                { label: t('vehicles.saleDate'), value: formatDay(sale.saleDate) },
                {
                  label: t('vehicles.buyer'),
                  value: sale.buyer === null ? t('common.notRecorded') : `${sale.buyer.name} · ${sale.buyer.phone}`,
                },
                { label: t('vehicles.requiredRetailAtSale'), value: formatMoney(sale.requiredRetailAtSale, { exact: true }) },
                {
                  label: t('vehicles.realizedContribution'),
                  value: (
                    <span className="inline-flex items-center gap-2">
                      <Money value={vehicle.realizedContribution?.amount ?? null} />
                      {vehicle.realizedContribution?.provisional ? <Badge tone="warning">{t('costs.provisional')}</Badge> : null}
                    </span>
                  ),
                },
                { label: t('vehicles.daysInStock'), value: vehicle.daysInStock === null ? t('common.notSet') : String(vehicle.daysInStock) },
              ]}
            />
            {sale.soldBelowRetail ? (
              <p className="rounded-md border border-[#a16207]/30 bg-[#a16207]/5 px-3 py-2 text-sm text-[#a16207]">
                {t('vehicles.soldBelowRetailNotice')}
                {sale.saleNote === null ? '' : ` — ${sale.saleNote}`}
              </p>
            ) : null}
            {unresolved.length === 0 ? null : (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">{t('vehicles.unresolvedCosts')}</p>
                <ul className="list-inside list-disc text-xs text-muted-foreground">
                  {unresolved.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              </div>
            )}
            <p className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">{t('vehicles.closedBanner')}</p>
          </section>
        )}

        {error === null ? null : (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <section className="space-y-3 rounded-lg border border-border bg-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-card-foreground">{t('vehicles.offerTargetTitle')}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t('vehicles.offerTargetBody')}</p>
            </div>
            {editable ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOfferOpen(true)}
              >
                {t('actions.edit')}
              </Button>
            ) : null}
          </div>
          <DefinitionList
            items={[
              { label: t('vehicles.profitMode'), value: t(`enums.profitMode.${vehicle.profitMode}`) },
              {
                label: vehicle.profitMode === 'MARGIN' ? t('vehicles.targetMargin') : t('vehicles.targetProfit'),
                value:
                  vehicle.profitMode === 'MARGIN'
                    ? formatPercent(inputs.targetMargin ?? null)
                    : formatMoney(inputs.targetProfit ?? null, { exact: true }),
              },
              { label: t('vehicles.riskBuffer'), value: formatMoney(inputs.riskBuffer ?? null, { exact: true }) },
              { label: t('vehicles.askingPrice'), value: formatMoney(vehicle.askingPrice) },
            ]}
          />
        </section>

        <CostItemsCard
          owner={{ type: 'VEHICLE', id: vehicle.id }}
          editable={editable}
          title={t('vehicles.workItemsTitle')}
          description={t('vehicles.workItemsBody')}
          onChanged={() => void refresh()}
        />

        <section className="space-y-3 rounded-lg border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-card-foreground">{t('vehicles.otherCostsTitle')}</h2>
          <p className="text-sm text-muted-foreground">{t('vehicles.otherCostsBody')}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {OVERRIDE_KEYS.map((key) => (
              <OverrideField
                key={key}
                paramKey={key}
                catalog={catalogQuery.data ?? null}
                templateValue={templateParams[key] ?? null}
                override={vehicle.paramOverrides[key]?.value ?? null}
                disabled={!editable || update.isPending}
                onSave={(value) => void saveOverride(key, value)}
              />
            ))}
          </div>
        </section>

        <section className="space-y-3 rounded-lg border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-card-foreground">{t('vehicles.automaticTitle')}</h2>
          <DefinitionList
            items={automatic.map((entry) => ({
              label: entry.label,
              value:
                entry.cost!.estimate === entry.cost!.resolved ? (
                  formatMoney(entry.cost!.resolved, { exact: true })
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <span className="text-muted-foreground line-through">{formatMoney(entry.cost!.estimate, { exact: true })}</span>
                    <span aria-hidden>→</span>
                    <span className="font-medium">{formatMoney(entry.cost!.resolved, { exact: true })}</span>
                  </span>
                ),
            }))}
          />
          <DefinitionList
            items={[
              { label: t('vehicles.holding'), value: formatMoney(outputs.holding ?? null, { exact: true }) },
              { label: t('vehicles.reconditioning'), value: formatMoney(outputs.repair ?? null, { exact: true }) },
            ]}
          />
          <p className="text-xs text-muted-foreground">{t('vehicles.holdingNote')}</p>
        </section>

        {vehicle.acquisitionType === 'TRADE_IN' ? (
          <section className="space-y-3 rounded-lg border border-border bg-card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-card-foreground">{t('vehicles.tradeInTitle')}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t('vehicles.tradeInScenarioOnly')}</p>
              </div>
              {editable ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTradeInOpen(true)}
                >
                  {vehicle.tradeIn === null ? t('actions.create') : t('actions.edit')}
                </Button>
              ) : null}
            </div>
            <DefinitionList
              items={[
                { label: t('vehicles.newVehicleProfit'), value: formatMoney(vehicle.tradeIn?.newVehicleProfit ?? null) },
                { label: t('vehicles.newVehicleMargin'), value: formatPercent(vehicle.tradeIn?.newVehicleMargin ?? null) },
                {
                  label: t('vehicles.demandLevel'),
                  value:
                    vehicle.tradeIn?.demandLevel === undefined || vehicle.tradeIn?.demandLevel === null
                      ? t('common.notSet')
                      : t(`enums.demandLevel.${vehicle.tradeIn.demandLevel}`),
                },
                {
                  label: t('vehicles.stockLevel'),
                  value:
                    vehicle.tradeIn?.stockLevel === undefined || vehicle.tradeIn?.stockLevel === null
                      ? t('common.notSet')
                      : t(`enums.stockLevel.${vehicle.tradeIn.stockLevel}`),
                },
                {
                  label: t('vehicles.supportRate'),
                  value: outputs.supportRate === null || outputs.supportRate === undefined ? '—' : formatPercent(outputs.supportRate),
                },
                { label: t('vehicles.support'), value: formatMoney(outputs.support ?? null) },
                {
                  label: t('vehicles.scenarioMaximum'),
                  value: formatMoney(
                    outputs.ceiling === null || outputs.ceiling === undefined ? null : (outputs.ceiling ?? 0) + (outputs.support ?? 0),
                  ),
                },
              ]}
            />
            <p className="text-xs text-muted-foreground">{t('vehicles.tradeInNeverInfluences')}</p>
          </section>
        ) : null}
      </div>

      <aside className="h-fit space-y-3 lg:sticky lg:top-6">
        <StatCard
          label={t('estimates.requiredRetail')}
          value={<Money value={outputs.retail ?? null} />}
          hint={t('vehicles.requiredRetailHint')}
        />
        <TotalCostCard
          outputs={outputs}
          inputs={inputs}
        />
        <StatCard
          label={t('vehicles.projectedProfit')}
          value={<Money value={outputs.profit ?? null} />}
          hint={t('vehicles.projectedProfitHint')}
        />
        {computeQuery.data?.provisional ? <Badge tone="warning">{t('costs.provisional')}</Badge> : null}
        {(outputs.retail ?? 0) > (outputs.appraisal ?? 0) ? (
          <p className="rounded-md border border-[#a16207]/30 bg-[#a16207]/5 px-3 py-2 text-xs text-[#a16207]">
            {t('vehicles.retailAboveAppraisal')}
          </p>
        ) : null}
        <p className="text-xs text-muted-foreground">
          {t('vehicles.provenanceNote', { source: t(`valuations.sources.${provenance.targetProfit?.source ?? 'TEMPLATE'}`) })}
        </p>
      </aside>

      <Sheet
        isOpen={isOfferOpen}
        onClose={() => setOfferOpen(false)}
        title={t('vehicles.offerTargetTitle')}
        description={t('vehicles.offerTargetBody')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setOfferOpen(false)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              isLoading={update.isPending}
              onClick={() => void saveOffer()}
            >
              {t('actions.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField
            id="offer-profit-mode"
            label={t('vehicles.profitMode')}
          >
            <Select
              id="offer-profit-mode"
              value={form.profitMode}
              onChange={(event) => setForm((current) => ({ ...current, profitMode: event.target.value }))}
              options={[
                { value: 'FIXED', label: t('enums.profitMode.FIXED') },
                { value: 'MARGIN', label: t('enums.profitMode.MARGIN') },
              ]}
            />
          </FormField>

          {form.profitMode === 'MARGIN' ? (
            <FormField
              id="offer-target-margin"
              label={t('vehicles.targetMargin')}
            >
              <Input
                id="offer-target-margin"
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={form.targetMarginPercent}
                onChange={(event) => setForm((current) => ({ ...current, targetMarginPercent: event.target.value }))}
              />
            </FormField>
          ) : (
            <FormField
              id="offer-target-profit"
              label={t('vehicles.targetProfit')}
            >
              <Input
                id="offer-target-profit"
                type="number"
                min={0}
                step={0.001}
                value={form.targetProfit}
                onChange={(event) => setForm((current) => ({ ...current, targetProfit: event.target.value }))}
              />
            </FormField>
          )}

          <FormField
            id="offer-risk-buffer"
            label={t('vehicles.riskBuffer')}
          >
            <Input
              id="offer-risk-buffer"
              type="number"
              min={0}
              step={0.001}
              value={form.riskBuffer}
              onChange={(event) => setForm((current) => ({ ...current, riskBuffer: event.target.value }))}
            />
          </FormField>

          <FormField
            id="offer-asking-price"
            label={t('vehicles.askingPrice')}
            hint={t('vehicles.askingPriceHint')}
          >
            <Input
              id="offer-asking-price"
              type="number"
              min={0}
              step={0.001}
              value={form.askingPrice}
              onChange={(event) => setForm((current) => ({ ...current, askingPrice: event.target.value }))}
            />
          </FormField>
        </div>
      </Sheet>

      <TradeInSheet
        isOpen={isTradeInOpen}
        vehicle={vehicle}
        tradeIn={vehicle.tradeIn}
        onClose={() => setTradeInOpen(false)}
        onSaved={refresh}
      />
    </div>
  );
};

const offerFormFrom = (vehicle: VehicleDetail): OfferForm => ({
  profitMode: vehicle.profitMode,
  targetProfit: vehicle.paramOverrides.targetProfit?.value === undefined ? '' : String(vehicle.paramOverrides.targetProfit.value),
  targetMarginPercent:
    vehicle.paramOverrides.targetMargin?.value === undefined ? '' : String(Math.round(vehicle.paramOverrides.targetMargin.value * 10_000) / 100),
  riskBuffer: vehicle.paramOverrides.riskBuffer?.value === undefined ? '' : String(vehicle.paramOverrides.riskBuffer.value),
  askingPrice: vehicle.askingPrice === null ? '' : String(vehicle.askingPrice),
});

/**
 * One overridable shared value: the template's figure, and this vehicle's own when it has one. "Reset to
 * template" clears the override rather than copying the template value in, so the vehicle keeps
 * following the template if an administrator changes it later (PP AC01).
 */
const OverrideField = ({
  paramKey,
  catalog,
  templateValue,
  override,
  disabled,
  onSave,
}: {
  paramKey: string;
  catalog: TemplateParamCatalogOutput | null;
  templateValue: number | null;
  override: number | null;
  disabled: boolean;
  onSave: (value: number | null) => void;
}) => {
  const { t } = useI18nTranslation();
  const [value, setValue] = useState(() => (override === null ? '' : String(override)));

  useEffect(() => setValue(override === null ? '' : String(override)), [override]);

  const definition = catalog?.params.find((entry) => entry.key === paramKey);

  return (
    <div className="space-y-1.5">
      <label
        htmlFor={`override-${paramKey}`}
        className="block text-sm font-medium text-card-foreground"
      >
        {definition?.label ?? t(`params.${paramKey}`, { defaultValue: paramKey })}
      </label>
      <div className="flex items-center gap-2">
        <Input
          id={`override-${paramKey}`}
          type="number"
          step={0.001}
          disabled={disabled}
          placeholder={templateValue === null ? '' : String(templateValue)}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onBlur={() => {
            const next = toOptionalNumber(value);

            if (next !== override) {
              onSave(next);
            }
          }}
        />
        {override === null ? null : (
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => onSave(null)}
          >
            {t('vehicles.resetToTemplate')}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {override === null
          ? t('vehicles.fromTemplate', { value: formatMoney(templateValue, { exact: true }) })
          : t('vehicles.overriddenFrom', { value: formatMoney(templateValue, { exact: true }) })}
      </p>
    </div>
  );
};

const Money = ({ value }: { value: number | null | undefined }) => (
  <span
    dir="ltr"
    className="tabular-nums"
  >
    {formatMoney(value)}
  </span>
);
